// DropdownMenu.js - Convertido a Tailwind CSS
import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMenu } from '../../contexts/MenuContext';
import { ChevronDown } from 'lucide-react';

const DropdownMenu = ({
  menu,
  onMenuClick,
  textColor = "text-white",
  hoverColor = "hover:text-accent-200",
  bgHover = "hover:bg-white/10",
  isMobile = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Usar el contexto de menú
  const { closeMenus, activeMenu, handleNavigation: contextHandleNavigation } = useMenu();
  const dropdownRef = useRef(null);

  // Determinar si este menú está abierto
  const isMenuOpen = activeMenu === menu.id;

  // Función para manejar navegación
  const handleNavigation = (path) => {
    navigate(path);
    if (contextHandleNavigation) {
      contextHandleNavigation(path);
    } else if (closeMenus) {
      closeMenus();
    }
  };

  // Función para verificar si un submenú está activo
  const isSubmenuActive = (submenuPath) => {
    return location.pathname === submenuPath || location.pathname.startsWith(submenuPath + '/');
  };

  // Función para verificar si el menú principal está activo
  const isMenuActive = () => {
    if (menu.path && location.pathname === menu.path) {
      return true;
    }

    if (menu.submenus && menu.submenus.length > 0) {
      return menu.submenus.some(submenu => isSubmenuActive(submenu.path));
    }

    return false;
  };

  // Manejar click en el menú principal
  const handleMenuClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Si es un menú simple (sin submenús), navegar directamente
    if (menu.path && (!menu.submenus || menu.submenus.length === 0)) {
      handleNavigation(menu.path);
      return;
    }

    // Si tiene submenús, toggle del dropdown
    if (onMenuClick) {
      onMenuClick(menu.id);
    }
  };

  // Manejar click en submenú
  const handleSubmenuClick = (e, submenuPath) => {
    e.preventDefault();
    e.stopPropagation();
    handleNavigation(submenuPath);
  };

  // Manejar click fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isMenuOpen && closeMenus) {
          closeMenus();
        }
      }
    };

    if (isMenuOpen) {
      // Agregar un pequeño delay para evitar cerrar inmediatamente
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenus]);

  // Manejar navegación por teclado
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMenuClick(e);
    } else if (e.key === 'Escape' && closeMenus) {
      closeMenus();
    } else if (e.key === 'ArrowDown' && menu.submenus && menu.submenus.length > 0) {
      e.preventDefault();
      if (!isMenuOpen && onMenuClick) {
        onMenuClick(menu.id);
      }
      // Focus en el primer submenu
      setTimeout(() => {
        const firstSubmenu = dropdownRef.current?.querySelector('[role="menuitem"]');
        if (firstSubmenu) {
          firstSubmenu.focus();
        }
      }, 100);
    }
  };

  // Manejar navegación por teclado en submenús
  const handleSubmenuKeyDown = (e, submenuPath, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNavigation(submenuPath);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (closeMenus) {
        closeMenus();
      }
      // Volver el focus al botón principal
      const mainButton = dropdownRef.current?.querySelector('button');
      if (mainButton) {
        mainButton.focus();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextSubmenu = dropdownRef.current?.querySelectorAll('[role="menuitem"]')[index + 1];
      if (nextSubmenu) {
        nextSubmenu.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        // Si es el primer elemento, volver al botón principal
        const mainButton = dropdownRef.current?.querySelector('button');
        if (mainButton) {
          mainButton.focus();
        }
      } else {
        const prevSubmenu = dropdownRef.current?.querySelectorAll('[role="menuitem"]')[index - 1];
        if (prevSubmenu) {
          prevSubmenu.focus();
        }
      }
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${isMenuActive()
            ? 'bg-white/20 text-white'
            : `${textColor} ${hoverColor} ${bgHover}`
          } ${isMenuOpen ? 'bg-white/20 text-white' : ''}`}
        onClick={handleMenuClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isMenuOpen}
        aria-haspopup={menu.submenus && menu.submenus.length > 0 ? 'true' : 'false'}
        aria-label={`${menu.label}${menu.submenus && menu.submenus.length > 0 ? ' - Menú con submenús' : ''}`}
        tabIndex={0}
        id={`menu-${menu.id}`}
        type="button"
      >
        {/* Icono del menú */}
        {menu.icon && (
          <span className="text-lg flex-shrink-0" role="img" aria-hidden="true">
            {menu.icon}
          </span>
        )}

        {/* Label del menú */}
        <span className="text-sm font-medium">{menu.label}</span>

        {/* Flecha para submenús */}
        {menu.submenus && menu.submenus.length > 0 && (
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isMenuOpen ? 'rotate-180' : ''
            }`} />
        )}
      </button>

      {/* Dropdown Menu */}
      {menu.submenus && menu.submenus.length > 0 && isMenuOpen && (
        <div
          className={`
            absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 animate-fade-in
            ${isMobile
              ? 'left-0 right-0 w-full'
              : 'left-0 w-64 min-w-max'
            }
          `}
          role="menu"
          aria-labelledby={`menu-${menu.id}`}
        >
          {menu.submenus.map((submenu, index) => (
            <button
              key={submenu.id}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left text-sm transition-colors duration-200 ${isSubmenuActive(submenu.path)
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                } first:rounded-t-lg last:rounded-b-lg`}
              onClick={(e) => handleSubmenuClick(e, submenu.path)}
              onKeyDown={(e) => handleSubmenuKeyDown(e, submenu.path, index)}
              tabIndex={0}
              role="menuitem"
              aria-label={`${submenu.label} - ${menu.label}`}
              type="button"
            >
              {/* Icono del submenu */}
              {submenu.icon && (
                <span className="text-base flex-shrink-0" role="img" aria-hidden="true">
                  {submenu.icon}
                </span>
              )}

              {/* Label del submenu */}
              <span className="font-medium">{submenu.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;